import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import { useState } from 'react';
import WebGrid from '../../webgrid/src';
import {
    ContractData,
    ContractPaymentSettings,
    PaymentCalculation,
} from '../domain/paymentContracts';
import { parseContractBonusPercent } from '../domain/contractBonusOverride';

/**
 * One line of the payment breakdown, in the five columns the receipt uses.
 *
 * Only the commission decomposes into a unit, a quantity and a unit price — it is
 * `comision/zi × zile`. The other lines are single amounts, and their `unit`, `quantity`
 * and `unitPrice` are left as empty strings rather than a dash or a fabricated `1 ×`:
 * a unit price of "1200 for 1" implies a rate that does not exist.
 */
export interface PaymentDetailRow {
    /** Stable row id; the grid must not fall back to the row index. */
    key: string;
    name: string;
    unit: string;
    quantity: string;
    unitPrice: string;
    value: string;
}

export interface ContractSimulationProjection {
    simulatedCommission: number;
    totalAmount: number;
    targetDate: string;
    daysUntil: number;
}

interface ContractPaymentDetailsProps {
    contract: ContractData;
    calculation: PaymentCalculation | null;
    loading: boolean;
    settings: ContractPaymentSettings;
    onSettingsChange: (settings: ContractPaymentSettings) => void;
    projection: ContractSimulationProjection | null;
    simulationDate: Dayjs | null;
    onSimulationDateChange: (value: Dayjs | null) => void;
    simulationIncludeBaseAmount: boolean;
    onSimulationIncludeBaseAmountChange: (value: boolean) => void;
    simulationWithDiscount: boolean;
    onSimulationWithDiscountChange: (value: boolean) => void;
    /**
     * The page-level "simulate every contract" toggle. Omitted when more than one
     * panel is on screen, because it is a page concern and repeating one shared
     * checkbox in every panel reads as a per-contract setting when it is not.
     */
    simulateAllContracts?: { checked: boolean; onChange: (value: boolean) => void };
    onOpenExtensionHistory: () => void;
    onProcessPayment: () => void;
    /**
     * Shows the per-payment contract discount field (SuperBonusC, BL-04). Administrators only;
     * the backend refuses the override from anyone else.
     */
    canOverrideDiscount?: boolean;
    processing: boolean;
    formatCurrency: (value: number | null | undefined) => string;
    /**
     * Two decimals, unlike {@link formatCurrency}.
     *
     * A daily commission is often under one leu, so rounding it to whole units the way
     * every other money figure is rounded would print `0 LEI` as the unit price of a line
     * whose total is plainly not zero.
     */
    formatCommissionPerDay: (value: number | null | undefined) => string;
    t: TFunction;
}

/**
 * The payment details block for a single contract: its summary, the payment type and
 * discount controls, the date simulation and the resulting amount.
 *
 * Rendered once per selected contract, so every control here writes to that contract's
 * own settings — a selection may mix a redemption with a prolongation.
 */
const ContractPaymentDetails = ({
    contract,
    calculation,
    loading,
    settings,
    onSettingsChange,
    projection,
    simulationDate,
    onSimulationDateChange,
    simulationIncludeBaseAmount,
    onSimulationIncludeBaseAmountChange,
    simulationWithDiscount,
    onSimulationWithDiscountChange,
    simulateAllContracts,
    onOpenExtensionHistory,
    onProcessPayment,
    canOverrideDiscount = false,
    processing,
    formatCurrency,
    formatCommissionPerDay,
    t,
}: Readonly<ContractPaymentDetailsProps>) => {
    const isExtension = settings.paymentType === 'extension';
    // The typed text is kept apart from the setting, so a half-typed or invalid entry stays
    // visible without being priced.
    const [contractBonusText, setContractBonusText] = useState<string>(
        settings.contractBonusPercent === null ? '' : String(settings.contractBonusPercent),
    );
    const contractBonusInvalid = parseContractBonusPercent(contractBonusText) === undefined;
    const discountSourceName = calculation?.discountSource
        ? t(`payment.discountSource.${calculation.discountSource}`)
        : '';
    const discountSourceLabel = discountSourceName ? ` · ${discountSourceName}` : '';

    const handleContractBonusChange = (text: string) => {
        setContractBonusText(text);
        const parsed = parseContractBonusPercent(text);
        if (parsed !== undefined && parsed !== settings.contractBonusPercent) {
            onSettingsChange({ ...settings, contractBonusPercent: parsed });
        }
    };

    const total = (() => {
        if (!calculation) {
            return 0;
        }
        const base = isExtension ? calculation.totalExtension : calculation.totalIntegral;
        return settings.applyDiscount ? base : base + calculation.discount;
    })();

    const detailColumns = [
        {
            key: 'name',
            title: t('payment.detailsColumns.name'),
            dataIndex: 'name',
            sortable: false,
            filterable: false,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'unit',
            title: t('payment.detailsColumns.unit'),
            dataIndex: 'unit',
            sortable: false,
            filterable: false,
            width: 90,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'quantity',
            title: t('payment.detailsColumns.quantity'),
            dataIndex: 'quantity',
            sortable: false,
            filterable: false,
            width: 110,
            align: 'right' as const,
            headerAlign: 'right' as const,
        },
        {
            key: 'unitPrice',
            title: t('payment.detailsColumns.unitPrice'),
            dataIndex: 'unitPrice',
            sortable: false,
            filterable: false,
            width: 130,
            align: 'right' as const,
            headerAlign: 'right' as const,
        },
        {
            key: 'value',
            title: t('payment.detailsColumns.value'),
            dataIndex: 'value',
            sortable: false,
            filterable: false,
            width: 130,
            align: 'right' as const,
            headerAlign: 'right' as const,
        },
    ];

    /**
     * The breakdown lines, in the order they build up the amount.
     *
     * A redemption shows four: the sum lent, the commission it earned, any exchange-rate
     * difference and the loyalty discount. A prolongation shows two — it settles the
     * commission only, so there is no principal and no exchange-rate difference to
     * reconcile.
     *
     * The row set is fixed, not conditional. A zero exchange-rate difference and an
     * unclaimed discount are printed as `0` rather than dropped, so the table has the same
     * shape on every contract: an operator reading two receipts side by side compares the
     * same four lines instead of working out which one is missing and why.
     */
    const detailRows: PaymentDetailRow[] = (() => {
        if (!calculation) {
            return [];
        }

        const blank = { unit: '', quantity: '', unitPrice: '' };
        const rows: PaymentDetailRow[] = [];

        // Order matters: the two amounts that restate the principal sit together at the
        // top, and the commission -- the only line with a real rate -- follows them.
        if (!isExtension) {
            const baseAmount = formatCurrency(calculation.baseAmount);
            const exchangeRateDifference = formatCurrency(calculation.exchangeRateDifference);

            rows.push({
                key: 'baseAmount',
                name: t('payment.detailsRows.creditRepayment'),
                unit: '',
                quantity: '',
                // One-off amounts carry the figure in both columns: there is no rate to
                // show, and a blank unit price beside a filled value reads as missing data.
                unitPrice: baseAmount,
                value: baseAmount,
            });

            rows.push({
                key: 'exchangeRateDifference',
                name: t('payment.exchangeRateDiff'),
                unit: '',
                quantity: '',
                unitPrice: exchangeRateDifference,
                value: exchangeRateDifference,
            });
        }

        rows.push({
            key: 'commission',
            name: t('payment.detailsRows.commissionAsAgreed'),
            unit: t('payment.days'),
            quantity: String(calculation.daysElapsed),
            unitPrice: formatCommissionPerDay(calculation.commissionPerDay),
            value: formatCurrency(calculation.commission),
        });

        // Only when a promotion applies: it is the exception, and a promotion row that is
        // always zero would read as a promotion every contract is missing out on.
        const promotion = calculation.promotion;
        if (promotion) {
            rows.push({
                key: 'promotion',
                name: t(`payment.promotion.${promotion.type}`),
                unit: t('payment.days'),
                quantity: String(promotion.freeDays),
                unitPrice: formatCommissionPerDay(calculation.commissionPerDay),
                value: `-${formatCurrency(promotion.amount)}`,
            });
        }

        // Shown whether or not a discount applies. When it does not, the amount really is
        // zero -- the total already reflects that, since it adds the discount back when
        // the operator declines it.
        const appliedDiscount = settings.applyDiscount ? calculation.discount : 0;
        rows.push({
            key: 'discount',
            name: t('payment.discount'),
            ...blank,
            value: appliedDiscount > 0 ? `-${formatCurrency(appliedDiscount)}` : formatCurrency(0),
        });

        return rows;
    })();

    return (
        <Box data-testid={`payment-panel-${contract.NrContract}`} sx={{ mb: 4 }}>
            <Paper sx={{ p: 1.5, mb: 2 }}>
                {loading && <Typography>{t('common.loading')}</Typography>}
                {!loading && calculation && (
                    <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {t('payment.selectedContract')}:
                        </Typography>
                        <Typography>
                            <strong>{t('contracts.number')}:</strong> {contract.NrContract}
                        </Typography>
                        <Typography>
                            <strong>{t('payment.daysElapsed')}:</strong> {calculation.daysElapsed}
                        </Typography>
                        <Typography>
                            <strong>{t('payment.extensions')}:</strong>{' '}
                            {calculation.currentExtensions} /{' '}
                            {calculation.maxExtensions ?? t('payment.unlimitedExtensions')}
                        </Typography>
                        {calculation.currentExtensions > 0 && (
                            <Tooltip title={t('payment.extensionHistory.title')}>
                                <IconButton
                                    size="small"
                                    color="info"
                                    onClick={onOpenExtensionHistory}
                                >
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                )}
            </Paper>

            {calculation && (
                <>
                    {/* Payment Type and Simulation Cards Row */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                        <Card sx={{ flex: 1 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {t('payment.paymentType')}
                                </Typography>

                                <RadioGroup
                                    value={settings.paymentType}
                                    onChange={event =>
                                        onSettingsChange({
                                            ...settings,
                                            paymentType: event.target.value as 'full' | 'extension',
                                        })
                                    }
                                >
                                    <FormControlLabel
                                        value="full"
                                        control={<Radio />}
                                        label={t('payment.fullPayment')}
                                    />
                                    <FormControlLabel
                                        value="extension"
                                        control={<Radio />}
                                        label={t('payment.extensionPayment')}
                                        disabled={!calculation.canExtend}
                                    />
                                </RadioGroup>

                                {isExtension && (
                                    <TextField
                                        label={t('payment.extensionDays')}
                                        type="number"
                                        value={settings.extensionDays}
                                        onChange={event =>
                                            onSettingsChange({
                                                ...settings,
                                                extensionDays:
                                                    Number.parseInt(event.target.value) || 30,
                                            })
                                        }
                                        sx={{ mt: 2 }}
                                        fullWidth
                                    />
                                )}

                                {calculation.discountPercent > 0 && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={settings.applyDiscount}
                                                onChange={event =>
                                                    onSettingsChange({
                                                        ...settings,
                                                        applyDiscount: event.target.checked,
                                                    })
                                                }
                                            />
                                        }
                                        label={`${t('payment.applyDiscount')} (${calculation.discountPercent}%${discountSourceLabel})`}
                                        sx={{ mt: 2 }}
                                    />
                                )}

                                {canOverrideDiscount && (
                                    <TextField
                                        label={t('payment.contractBonus.label')}
                                        value={contractBonusText}
                                        onChange={event =>
                                            handleContractBonusChange(event.target.value)
                                        }
                                        error={contractBonusInvalid}
                                        helperText={
                                            contractBonusInvalid
                                                ? t('payment.contractBonus.invalid')
                                                : t('payment.contractBonus.helper')
                                        }
                                        slotProps={{
                                            htmlInput: {
                                                inputMode: 'numeric',
                                                'data-testid': `contract-bonus-${contract.Nr}`,
                                            },
                                        }}
                                        size="small"
                                        sx={{ mt: 2 }}
                                        fullWidth
                                    />
                                )}
                            </CardContent>
                        </Card>

                        {/* Payment Simulation Card */}
                        <Card sx={{ flex: 1, backgroundColor: '#f5f5f5' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {t('payment.simulation.title')}
                                </Typography>

                                <Stack spacing={2}>
                                    <DatePicker
                                        label={t('payment.simulation.selectDate')}
                                        value={simulationDate}
                                        onChange={newValue => onSimulationDateChange(newValue)}
                                        slotProps={{
                                            textField: { size: 'small', fullWidth: true },
                                        }}
                                    />

                                    {simulateAllContracts && (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={simulateAllContracts.checked}
                                                    onChange={event =>
                                                        simulateAllContracts.onChange(
                                                            event.target.checked,
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={t('payment.simulation.allContracts')}
                                        />
                                    )}

                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={simulationIncludeBaseAmount}
                                                    onChange={event =>
                                                        onSimulationIncludeBaseAmountChange(
                                                            event.target.checked,
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={t('payment.simulation.includeBaseAmount')}
                                            sx={{ ml: 0 }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={simulationWithDiscount}
                                                    onChange={event =>
                                                        onSimulationWithDiscountChange(
                                                            event.target.checked,
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={t('payment.simulation.applyDiscount')}
                                        />
                                    </Box>

                                    {projection && (
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                backgroundColor: '#fff',
                                                borderRadius: 1,
                                                border: '1px solid #ddd',
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                color="text.secondary"
                                                gutterBottom
                                            >
                                                {t('payment.simulation.projectionFor')}{' '}
                                                {projection.targetDate}
                                                {projection.daysUntil > 0 &&
                                                    ` (+${projection.daysUntil} ${t('payment.simulation.days')})`}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>{t('contracts.number')}:</strong>{' '}
                                                {contract.NrContract}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>
                                                    {t('payment.simulation.simulatedCommission')}:
                                                </strong>{' '}
                                                +{formatCurrency(projection.simulatedCommission)}
                                            </Typography>
                                            <Typography variant="h5" color="primary" sx={{ mt: 1 }}>
                                                {formatCurrency(projection.totalAmount)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {!projection && simulationDate && (
                                        <Typography variant="body2" color="text.secondary">
                                            {t('payment.simulation.selectContractFirst')}
                                        </Typography>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>

                    <Paper sx={{ mb: 3 }}>
                        <WebGrid<PaymentDetailRow>
                            data={detailRows}
                            columns={detailColumns}
                            // No checkbox: these are the lines making up one amount, not
                            // records to act on.
                            selectable={false}
                            // At most four rows, in a deliberate order that reads as a
                            // calculation. Sorting or paginating them would only break it.
                            sortable={false}
                            pagination={false}
                            filterBar={false}
                            exportable={false}
                            onExport={() => {}}
                            rowIdAccessor={row => row.key}
                            title={
                                isExtension
                                    ? t('payment.extensionPaymentDetails')
                                    : t('payment.fullPaymentDetails')
                            }
                            emptyMessage=""
                        />

                        <Stack spacing={0.5} sx={{ px: 2, py: 1.5 }}>
                            <Typography>
                                <strong>{t('contracts.number')}:</strong> {contract.NrContract}
                            </Typography>
                            {/* The total stays outside the grid deliberately: it is the
                                authoritative amount the backend will charge, so it must not
                                be a figure the grid computed from the rows above and could
                                disagree with. */}
                            <Typography variant="h6">
                                <strong>{t('payment.total')}:</strong> {formatCurrency(total)}
                            </Typography>
                        </Stack>
                    </Paper>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={onProcessPayment}
                        disabled={processing}
                        data-testid={`process-payment-${contract.NrContract}`}
                    >
                        {t('payment.processPayment')}
                    </Button>
                </>
            )}
        </Box>
    );
};

export default ContractPaymentDetails;
